import React, { useState } from 'react';
import { FaCode, FaRocket, FaArrowRight, FaGraduationCap } from 'react-icons/fa';

const Programs = () => {
  const [expandedProgram, setExpandedProgram] = useState(null);

  const programs = [
    {
      id: 'super50',
      title: 'Super 50 Program',
      icon: <FaRocket className="text-5xl" />,
      description: 'An intensive, quality-focused training initiative delivering an unparalleled educational experience',
      gradient: 'from-blue-600 to-purple-600',
      image: 'https://linkinprofessionals.com/images/super-1.jpg',
      features: [
        'Intensive 3-6 months training program',
        'Top 50 selected candidates per batch',
        'Industry-relevant curriculum',
        'Hands-on live project experience',
        'Dedicated mentorship from industry experts',
        'Guaranteed placement assistance',
        'Career counseling and guidance',
        'Technical and soft skills development'
      ],
      technologies: ['Java', 'Python', 'Full Stack Development', 'Data Structures', 'System Design', 'Problem Solving']
    },
    {
      id: 'bootcamp',
      title: 'Bootcamp',
      icon: <FaCode className="text-5xl" />,
      description: 'Fast-paced, immersive learning experiences with hands-on real projects and expert mentorship',
      gradient: 'from-orange-500 to-yellow-500',
      image: 'https://linkinprofessionals.com/images/super-2.png',
      features: [
        'Learning phase with skill acquisition',
        'Technologies: Angular, React, HTML, CSS',
        'AI, ML, IoT, Python training',
        '.NET Full Stack & Java Full Stack',
        'Real-world project implementation',
        'Industry certification preparation',
        'Expert instructors and mentorship',
        'Career support and alumni networking'
      ],
      technologies: ['Angular', 'React', 'HTML/CSS', 'Python', 'AI/ML', 'IoT', '.NET Full Stack', 'Java Full Stack']
    },
    {
      id: 'lms',
      title: 'LMS (Learning Management System)',
      icon: <FaGraduationCap className="text-5xl" />,
      description: 'Industry-driven digital learning platform for engineering students, accessible PAN India and beyond',
      gradient: 'from-green-500 to-teal-500',
      image: 'https://linkinprofessionals.com/images/super-3.png',
      features: [
        'Affordable and accessible education',
        'Expert guidance from industry professionals',
        'Bridge gap between students and industry',
        'Live project-based learning',
        'Swift industry readiness preparation',
        'Digital learning in modern landscape',
        'PAN India accessibility',
        'Practical exposure to real-world scenarios'
      ],
      technologies: ['Software Development', 'Real-time Projects', 'Technical Training', 'Soft Skills', 'Industry Tools']
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="programs" className="py-20 bg-white" data-testid="programs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Programs</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Choose the program that fits your career goals and start your journey to success
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {programs.map((program, index) => (
            <div
              key={program.id}
              className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-300"
              data-testid={`program-card-${index}`}
            >
              {/* Program Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={program.image}
                  alt={program.title}
                  className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${program.gradient} opacity-75`}></div>
                <div className="absolute inset-0 flex items-center justify-center text-white">
                  {program.icon}
                </div>
              </div>

              {/* Program Content */}
              <div className="p-8">
                <h3 className={`text-3xl font-bold mb-4 bg-gradient-to-r ${program.gradient} bg-clip-text text-transparent`}>
                  {program.title}
                </h3>
                <p className="text-gray-600 mb-6 text-lg">
                  {program.description}
                </p>

                {/* Features List */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Program Highlights:</h4>
                  <ul className="space-y-2">
                    {program.features.slice(0, expandedProgram === program.id ? program.features.length : 3).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-600">
                        <FaArrowRight className="text-blue-600 mt-1 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {program.features.length > 3 && (
                    <button
                      onClick={() => setExpandedProgram(expandedProgram === program.id ? null : program.id)}
                      className="text-blue-600 font-semibold mt-3 hover:text-blue-700"
                      data-testid={`expand-program-${index}`}
                    >
                      {expandedProgram === program.id ? 'Show Less' : 'Show More'}
                    </button>
                  )}
                </div>

                {/* Technologies */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Technologies Covered:</h4>
                  <div className="flex flex-wrap gap-2">
                    {program.technologies.map((tech, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r ${program.gradient} text-white`}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={scrollToContact}
                  className={`w-full bg-gradient-to-r ${program.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transform hover:-translate-y-1 transition-all flex items-center justify-center gap-2`}
                  data-testid={`program-enroll-btn-${index}`}
                >
                  Enroll Now
                  <FaArrowRight />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Programs;